import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  UserKeys,
  getGroups,
  getIndexedGroupsForAutocomplete,
  getIndexedUsers,
  getUserList,
  searchCalendars,
} from '../api/users-api';
import { useState } from 'react';
import useDebounce from '../utils/useDebounce';
import { CalendarIcon } from '@mui/x-date-pickers';
import { GroupHeader, GroupItems } from './autocomplete-groups-ui';
import UserAvatar from './user-avatar';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { emailRegex } from '../utils/regular-expressions';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import Check from '@mui/icons-material/Check';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import Group from '@mui/icons-material/Group';

interface SharedBigQueryAutocompleteProps {
  sources: ('users' | 'groups' | 'calendars')[];
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  setAutoCompleteTypeValue?: (value: string) => void;
  setClearWasClicked?: (value: boolean) => void;
  onSelection?: (value: string, type?: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  freeSolo?: boolean;
  errors?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  /** When false, user data is fetched from App Engine instead of BigQuery. */
  useBigQueryForUsers: boolean;
  /** When false, group data is fetched from App Engine instead of BigQuery. */
  useBigQueryForGroups: boolean;
  autoFocus?: boolean;
}

export default function SharedBigQueryAutocomplete({
  size,
  disabled,
  freeSolo,
  autocompleteValue,
  setAutoCompleteValue,
  setClearWasClicked,
  onSelection,
  sources,
  setAutoCompleteTypeValue,
  errors,
  helperText,
  label,
  required,
  useBigQueryForUsers,
  useBigQueryForGroups,
  autoFocus,
}: SharedBigQueryAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(
    freeSolo ? autocompleteValue : inputValue,
    500
  );

  const usersEnabled =
    debouncedInput !== '' &&
    sources.includes('users') &&
    autocompleteValue === '';

  const freeSoloUserEnabled =
    debouncedInput !== '' && sources.includes('users');

  const userQueryEnabled = freeSolo ? freeSoloUserEnabled : usersEnabled;

  const { data: userDataFromBigQuery, isFetching: userLoadingFromBigQuery } =
    useQuery({
      queryKey: [UserKeys.UserList, { debouncedInput }],
      queryFn: () => {
        const filters: GridFilterModel = {
          items: [
            {
              field: 'userEmail',
              operator: 'contains',
              value: debouncedInput,
            },
          ],
        };
        const sortItems: GridSortModel = [
          {
            field: 'userEmail',
            sort: 'asc',
          },
        ];
        return getIndexedUsers(
          {
            results: [],
            pageSize: 5,
            currentPage: 0,
            totalCount: 0,
          },
          filters,
          sortItems
        );
      },
      enabled: userQueryEnabled && useBigQueryForUsers,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
    });

  const { data: userDataFromAppEngine, isFetching: userLoadingFromAppEngine } =
    useQuery({
      queryKey: [UserKeys.UserList, { debouncedInput }],
      queryFn: () =>
        getUserList(
          {
            cursor: null,
            results: [],
            nameSpace: '',
            firstPull: true,
            searchTime: 0,
            pageSize: 5,
          },
          debouncedInput,
          '',
          null,
          null
        ),
      enabled: userQueryEnabled && !useBigQueryForUsers,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
    });

  const userData = useBigQueryForUsers
    ? userDataFromBigQuery
    : userDataFromAppEngine;
  const userLoading = useBigQueryForUsers
    ? userLoadingFromBigQuery
    : userLoadingFromAppEngine;

  const groupsEnabled =
    debouncedInput !== '' &&
    sources.includes('groups') &&
    autocompleteValue === '';

  const freeSoloGroupEnabled =
    debouncedInput !== '' && sources.includes('groups');

  const groupQueryEnabled = freeSolo ? freeSoloGroupEnabled : groupsEnabled;

  const { data: groupDataFromBigQuery, isFetching: groupsLoadingFromBigQuery } =
    useQuery({
      queryKey: [UserKeys.Groups, 'bigquery', { debouncedInput }],
      queryFn: () =>
        getIndexedGroupsForAutocomplete(
          {
            results: [],
            pageSize: 5,
            currentPage: 0,
            totalCount: 0,
          },
          debouncedInput
        ),
      enabled: groupQueryEnabled && useBigQueryForGroups,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
    });

  const {
    data: groupDataFromAppEngine,
    isFetching: groupsLoadingFromAppEngine,
  } = useQuery({
    queryKey: [UserKeys.Groups, { debouncedInput }],
    queryFn: () =>
      getGroups(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput
      ),
    enabled: groupQueryEnabled && !useBigQueryForGroups,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const groupData = useBigQueryForGroups
    ? groupDataFromBigQuery
    : groupDataFromAppEngine;
  const groupsLoading = useBigQueryForGroups
    ? groupsLoadingFromBigQuery
    : groupsLoadingFromAppEngine;

  const calendarEnabled =
    debouncedInput !== '' &&
    sources.includes('calendars') &&
    autocompleteValue === '';

  const { data: calendarData, isFetching: calendarsLoading } = useQuery({
    queryKey: [UserKeys.CalendarSearch, { debouncedInput }],
    queryFn: () => searchCalendars(debouncedInput),
    enabled: calendarEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });
  interface Option {
    email: string;
    type: string;
    firstName?: string;
    lastName?: string;
    suspended?: boolean;
    archived?: boolean;
    directMembersCount?: number;
  }

  const userOptions: Option[] = sources.includes('users')
    ? userData?.results.map((result) => {
        return {
          email: result.userEmail,
          type: 'user',
          firstName: result.firstName,
          lastName: result.lastName,
          suspended: result.suspended,
          archived: result.archived,
        };
      }) || []
    : [];
  const groupOptions: Option[] = sources.includes('groups')
    ? groupData?.results.map((result) => {
        return {
          email: result.email,
          type: 'group',
          directMembersCount:
            (result as { directMembersCount?: number }).directMembersCount ??
            (result as { directMemberCount?: number }).directMemberCount,
        };
      }) || []
    : [];
  const calendarOptions: Option[] = sources.includes('calendars')
    ? calendarData?.map((result) => {
        return {
          email: result.summary,
          firstName: result.displayString.split('||')[0],
          type: 'calendar',
        };
      }) || []
    : [];
  const freeSoloOption: Option[] =
    freeSolo &&
    autocompleteValue &&
    emailRegex.test(autocompleteValue) &&
    ![...userOptions, ...groupOptions, ...calendarOptions].some(
      (option) => option.email === autocompleteValue
    )
      ? [
          {
            email: autocompleteValue,
            type: 'user',
            firstName: '',
            lastName: '',
          },
        ]
      : [];
  const options = [
    ...userOptions,
    ...groupOptions,
    ...calendarOptions,
    ...freeSoloOption,
  ];

  function createNoOptionsText() {
    let text = 'No results found.';
    if (debouncedInput === '') {
      text = 'Start typing to search...';
    } else if (userOptions.length === 0 && groupData?.results.length === 0) {
      text = 'No results found.';
    }

    return text;
  }

  function createLabelText() {
    let text = 'Search for a user or group';
    if (sources.includes('users') && sources.includes('groups')) {
      text = 'Search for a user or group';
    } else if (sources.includes('users') && sources.includes('calendars')) {
      text = 'Search for a user or specific calendar';
    } else if (sources.includes('users')) {
      text = 'Search for a user';
    } else if (sources.includes('groups')) {
      text = 'Search for a group';
    }

    text = label || text;

    return text;
  }

  const SmallAvatar = styled(Avatar)(() => ({
    width: 16,
    height: 16,
    backgroundColor: 'white',
  }));

  return (
    <Autocomplete
      id="shared-autocomplete"
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 350,
          },
        },
      }}
      autoComplete
      autoHighlight
      freeSolo={freeSolo}
      includeInputInList
      filterSelectedOptions={!freeSolo}
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return (
            userData?.results.find((user) => user.userEmail === option)
              ?.userEmail || option
          );
        } else {
          return option.email;
        }
      }}
      isOptionEqualToValue={(option, value) => {
        const valString =
          typeof value === 'string' ? value : value?.email || '';
        if (option.type === 'calendar') {
          return option.firstName === valString;
        }
        return option.email === valString;
      }}
      blurOnSelect={true}
      value={autocompleteValue}
      onChange={(_event, newValue, reason) => {
        let selectedValue = '';
        let selectedType = '';

        if (reason === 'clear') {
          setClearWasClicked?.(true);
          setAutoCompleteValue('');
          if (onSelection) {
            onSelection('');
          }
          return;
        }

        if (!freeSolo) {
          if (typeof newValue === 'string') {
            setAutoCompleteValue(newValue);
            selectedValue = newValue;
            selectedType = '';
          } else {
            selectedType = newValue?.type ?? '';
            if (setAutoCompleteTypeValue) {
              setAutoCompleteTypeValue(selectedType);
            }

            if (newValue?.type === 'calendar') {
              selectedValue = newValue?.firstName || '';
              setAutoCompleteValue(newValue?.firstName || '');
            } else {
              selectedValue = newValue?.email || '';
              setAutoCompleteValue(newValue?.email || '');
            }
          }
        } else {
          // freeSolo mode: handle both string and object values
          if (typeof newValue === 'string') {
            setAutoCompleteValue(newValue);
            selectedValue = newValue;
            selectedType = '';
          } else if (newValue !== null) {
            selectedValue = newValue.email || newValue.firstName || '';
            selectedType = newValue?.type ?? '';
            setAutoCompleteValue(selectedValue);
            setAutoCompleteTypeValue?.(selectedType);
          }
        }

        // Call onSelection for valid selections in both modes
        if (
          selectedValue &&
          (reason === 'selectOption' || reason === 'createOption') &&
          onSelection
        ) {
          onSelection(selectedValue, selectedType);
        }
      }}
      renderOption={(props, option: Option) => {
        const { key, ...optionProps } = props as typeof props & { key: string };
        return (
          <ListItem key={key} {...optionProps} dense>
            {option.type === 'user' ? (
              <>
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <SmallAvatar
                        alt={`${option.firstName} ${option.lastName}`}
                        sx={{
                          border: (theme) =>
                            `1px solid ${
                              option.suspended
                                ? theme.palette.warning.main
                                : option.archived
                                ? theme.palette.info.main
                                : theme.palette.success.main
                            }`,
                        }}
                      >
                        {option.suspended ? (
                          <DoNotDisturb color="warning" sx={{ width: 10 }} />
                        ) : option.archived ? (
                          <ArchiveRounded color="info" sx={{ width: 10 }} />
                        ) : (
                          <Check color="success" sx={{ width: 10 }} />
                        )}
                      </SmallAvatar>
                    }
                    sx={{ mr: 2 }}
                  >
                    <UserAvatar userEmail={option.email} />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={`${option.firstName} ${option.lastName}`}
                />
              </>
            ) : option.type === 'group' ? (
              <>
                <ListItemAvatar>
                  <Avatar>
                    <Group />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={`${option.directMembersCount} members`}
                  primaryTypographyProps={{ whiteSpace: 'normal' }}
                />
              </>
            ) : (
              <>
                <ListItemAvatar>
                  <Avatar>
                    <CalendarIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={option.firstName}
                  primaryTypographyProps={{ whiteSpace: 'normal' }}
                />
              </>
            )}
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={createLabelText()}
          required={required}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {userLoading || groupsLoading || calendarsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
        />
      )}
      inputValue={freeSolo ? autocompleteValue : inputValue}
      onInputChange={(_event, newInputValue) => {
        if (freeSolo) {
          setAutoCompleteValue(newInputValue);
        } else {
          setInputValue(newInputValue);
        }
      }}
      groupBy={(option) => option.type}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>
            {params.group === 'user'
              ? 'Users'
              : params.group === 'group'
              ? 'Groups'
              : 'Calendars'}
          </GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
    />
  );
}
